<template>
  <div class="evidence-condition-card gm-card rounded-xl pa-5 mb-4 border">
    <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-3">
      <div class="d-flex align-center ga-2">
        <div class="icon-circle bg-blue-50 d-flex align-center justify-center rounded-lg">
          <v-icon color="#2655f1" size="20">mdi-table-check</v-icon>
        </div>
        <div>
          <h3 class="text-subtitle-1 font-weight-bold text-slate-800 mb-0">
            确定性证据逐条判定
          </h3>
          <div class="text-caption text-slate-500">
            {{ conditionSummaryText }}
          </div>
        </div>
      </div>

      <!-- 判定规则口径说明 -->
      <div v-if="ruleStatement" class="rule-statement-pill px-3 py-1-5 rounded-lg text-caption border bg-slate-50 text-slate-700">
        <span class="font-weight-bold text-slate-900">组合逻辑：</span>
        {{ ruleStatement }}
      </div>
    </div>

    <!-- 未评估状态（如 unsupported / unresolved） -->
    <div v-if="!evaluated" class="unsupported-box pa-4 rounded-lg d-flex align-center ga-3">
      <v-icon color="#78909c" size="28">mdi-information-outline</v-icon>
      <div>
        <div class="font-weight-medium text-slate-700">未触发取证判定</div>
        <div class="text-caption text-slate-500 mt-0-5">
          {{ reasonText || '该告警未做取证，原因通常为本期未配置证据链规则或设备坐标无法解析。' }}
        </div>
      </div>
    </div>

    <!-- 正常已取证表格 -->
    <div v-else class="table-responsive rounded-xl overflow-hidden border">
      <table class="condition-table w-100">
        <thead>
          <tr>
            <th style="min-width: 140px">测点条件</th>
            <th style="min-width: 110px">触发判定口径</th>
            <th style="min-width: 100px">判定结论</th>
            <th style="min-width: 160px">窗口极值与占比</th>
            <th style="min-width: 120px">采样波动范围</th>
            <th style="min-width: 130px">告警时刻瞬间值</th>
            <th style="min-width: 70px">超阈值</th>
            <th style="min-width: 240px">智能体事实解读</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cond in conditions" :key="cond.conditionId" :class="getConditionRowClass(cond)">
            <!-- 测点条件 -->
            <td>
              <div class="font-weight-bold text-slate-800">{{ cond.label || cond.point }}</div>
              <div class="text-caption text-slate-400 mt-0-5">{{ cond.point }}</div>
            </td>

            <!-- 判定口径与换算说明 -->
            <td>
              <div class="font-weight-bold text-slate-800">
                {{ cond.op }} {{ cond.threshold }} {{ cond.unit || '' }}
              </div>
              <div v-if="cond.transform" class="mt-0-5">
                <v-tooltip :text="getTransformText(cond.transform)" location="top" max-width="360">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="transform-chip text-caption px-1-5 py-0-5 rounded d-inline-flex align-center ga-1 cursor-pointer">
                      <v-icon size="12" color="#4f46e5">mdi-function</v-icon>
                      {{ cond.transform }}
                    </span>
                  </template>
                </v-tooltip>
              </div>
            </td>

            <!-- 判定结果徽章 -->
            <td>
              <span class="outcome-badge d-inline-flex align-center ga-1 px-2-5 py-1 rounded-lg text-caption font-weight-bold" :class="getOutcomeClass(cond.outcome)">
                <v-icon size="13">{{ getOutcomeIcon(cond.outcome) }}</v-icon>
                {{ getOutcomeText(cond.outcome) }}
              </span>
              <div v-if="cond.outcome === 'unknown' && cond.unknownKind" class="text-caption text-slate-400 mt-1">
                {{ getUnknownKindText(cond.unknownKind) }}
              </div>
            </td>

            <!-- 窗口极值与安全裕度进度条 -->
            <td>
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="font-weight-bold text-slate-800">
                  {{ formatNumber(cond.comparedExtreme) }} {{ cond.comparedExtreme != null ? cond.unit : '' }}
                </span>
                <span v-if="cond.windowMaxPercent != null" class="text-caption font-weight-bold" :class="getPercentColorClass(cond.windowMaxPercent)">
                  {{ cond.windowMaxPercent }}%
                </span>
              </div>
              <!-- 占阈值百分比进度条 -->
              <div v-if="cond.windowMaxPercent != null" class="percent-track rounded-pill">
                <div
                  class="percent-bar rounded-pill"
                  :style="{ width: `${Math.min(cond.windowMaxPercent, 100)}%` }"
                  :class="getPercentBarClass(cond.windowMaxPercent)"
                />
              </div>
              <div v-if="cond.recordTime" class="text-caption text-slate-400 mt-1">
                极值时间：{{ formatRecordTime(cond.recordTime) }}
              </div>
            </td>

            <!-- 采样波动范围 (valueRange) -->
            <td>
              <div v-if="getValueRange(cond.conditionId)" class="text-caption font-weight-medium text-slate-700">
                {{ getValueRange(cond.conditionId) }}
              </div>
              <div v-else class="text-caption text-slate-400">
                —
              </div>
            </td>

            <!-- 告警时刻瞬间值 -->
            <td>
              <div class="font-weight-bold text-slate-800">
                {{ formatNumber(cond.alarmNearestCompared) }} {{ cond.alarmNearestCompared != null ? cond.unit : '' }}
              </div>
              <div v-if="cond.alarmNearestOffsetSec != null" class="text-caption text-slate-400 mt-0-5">
                时刻偏差：{{ cond.alarmNearestOffsetSec > 0 ? '+' : '' }}{{ cond.alarmNearestOffsetSec }}s
              </div>
              <div v-else class="text-caption text-slate-400 mt-0-5">
                瞬时未采到
              </div>
            </td>

            <!-- 超阈值点数（0 是明确事实，不可为 —） -->
            <td>
              <span class="font-weight-bold px-2 py-0-5 rounded text-caption" :class="cond.hitCount > 0 ? 'hit-count-positive' : 'hit-count-zero'">
                {{ cond.hitCount != null ? cond.hitCount : '—' }}
              </span>
            </td>

            <!-- 自然语言解读 -->
            <td>
              <div class="narrative-box pa-2 rounded-lg text-caption text-slate-700">
                {{ getNarrative(cond.conditionId) }}
              </div>
            </td>
          </tr>
          <tr v-if="!conditions.length">
            <td colspan="8" class="text-center text-disabled py-8">暂无条件判定记录</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  evidence: { type: Object, default: null },
  diagnosis: { type: Object, default: null },
  analysis: { type: Object, default: null },
})

const evaluated = computed(() => Boolean(props.evidence?.evaluated))
const reasonText = computed(() => props.evidence?.reason || '')
const conditions = computed(() => props.evidence?.conditions || [])
const ruleStatement = computed(() => props.analysis?.ruleStatement || '')

const conditionSummaryText = computed(() => {
  if (!props.evidence) return ''
  const need = props.evidence.need ?? 2
  const met = props.evidence.met ?? 0
  const unknown = props.evidence.unknown ?? 0
  return `规则设定需命中 ${need} 项，当前实际复现 ${met} 项，未能取证 ${unknown} 项`
})

// 自然语言解读映射
const narrativeMap = computed(() => {
  const map = {}
  const list = props.diagnosis?.conditionNarratives || []
  list.forEach(item => {
    if (item.conditionId) {
      map[item.conditionId] = item.narrative
    }
  })
  return map
})

function getNarrative(conditionId) {
  return narrativeMap.value[conditionId] || '—'
}

// 波动区间 (valueRange)
const valueRangeMap = computed(() => {
  const map = {}
  const list = props.analysis?.conditions || []
  list.forEach(item => {
    if (item.conditionId && item.valueRange) {
      const vr = item.valueRange
      const min = vr.comparedMin != null ? vr.comparedMin : vr.rawMin
      const max = vr.comparedMax != null ? vr.comparedMax : vr.rawMax
      if (min != null && max != null) {
        map[item.conditionId] = `${min} ~ ${max}`
      }
    }
  })
  return map
})

function getValueRange(conditionId) {
  return valueRangeMap.value[conditionId] || null
}

// 换算规则说明
const transformMap = computed(() => {
  const map = {}
  const list = props.analysis?.transformStatements || []
  list.forEach(t => {
    if (t.transform) {
      map[t.transform] = t.text
    }
  })
  return map
})

function getTransformText(transform) {
  return transformMap.value[transform] || `已应用 ${transform} 换算`
}

function formatNumber(val) {
  if (val == null || val === '') return '—'
  const num = Number(val)
  return Number.isNaN(num) ? '—' : Number(num.toFixed(2))
}

function formatRecordTime(isoTime) {
  if (!isoTime) return ''
  // 去除时区并取时分秒
  const parts = String(isoTime).split('T')
  if (parts.length > 1) {
    return parts[1].slice(0, 8)
  }
  return String(isoTime).slice(11, 19)
}

function getOutcomeText(outcome) {
  switch (outcome) {
    case 'met':
      return '已超阈值'
    case 'not_met':
      return '未超阈值'
    case 'unknown':
      return '未能取证'
    default:
      return outcome || '—'
  }
}

function getOutcomeIcon(outcome) {
  switch (outcome) {
    case 'met':
      return 'mdi-alert-circle'
    case 'not_met':
      return 'mdi-checkbox-marked-circle-outline'
    case 'unknown':
      return 'mdi-help-circle-outline'
    default:
      return 'mdi-circle-small'
  }
}

function getOutcomeClass(outcome) {
  switch (outcome) {
    case 'met':
      return 'outcome-met'
    case 'not_met':
      return 'outcome-not-met'
    case 'unknown':
      return 'outcome-unknown'
    default:
      return ''
  }
}

function getUnknownKindText(kind) {
  switch (kind) {
    case 'no_data':
      return '窗口内无采样'
    case 'truncated':
      return '取数触顶截断'
    case 'link_or_config':
      return '链路/配置异常'
    default:
      return kind
  }
}

function getConditionRowClass(cond) {
  if (cond.outcome === 'met') return 'row-met'
  if (cond.outcome === 'unknown') return 'row-unknown'
  return ''
}

function getPercentColorClass(percent) {
  if (percent >= 100) return 'text-red-darken-3'
  if (percent >= 50) return 'text-amber-darken-3'
  return 'text-blue-darken-3'
}

function getPercentBarClass(percent) {
  if (percent >= 100) return 'bar-red'
  if (percent >= 50) return 'bar-amber'
  return 'bar-blue'
}
</script>

<style scoped lang="scss">
.evidence-condition-card {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
}

.icon-circle {
  width: 36px;
  height: 36px;
}

.rule-statement-pill {
  border-color: #e2e8f0;
}

.transform-chip {
  background: #eef2ff;
  color: #3730a3;
  border: 1px solid #c7d2fe;
  font-weight: 500;
}

.condition-table {
  border-collapse: collapse;
  font-size: 13px;

  th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    text-align: left;
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tr:hover td {
    background: #f8fafc;
  }

  tr.row-met td {
    background: #fff8f8;
  }
}

.outcome-badge {
  border: 1px solid transparent;

  &.outcome-met {
    background: #fee2e2;
    color: #b91c1c;
    border-color: #fecaca;
  }

  &.outcome-not-met {
    background: #eef2ff;
    color: #3730a3;
    border-color: #c7d2fe;
  }

  /* 法定红线：unknown 严禁绿色，必须使用平静灰 */
  &.outcome-unknown {
    background: #f1f5f9;
    color: #475569;
    border-color: #cbd5e1;
  }
}

.percent-track {
  width: 100%;
  height: 5px;
  background: #f1f5f9;
  overflow: hidden;
}

.percent-bar {
  height: 100%;
  transition: width 0.3s ease;

  &.bar-red {
    background: #ef4444;
  }
  &.bar-amber {
    background: #f59e0b;
  }
  &.bar-blue {
    background: #3b82f6;
  }
}

.hit-count-positive {
  background: #fee2e2;
  color: #b91c1c;
}

.hit-count-zero {
  background: #f1f5f9;
  color: #64748b;
}

.narrative-box {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  line-height: 1.5;
}
</style>
